import { ChevronDown, ExternalLink } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import type { ReactElement } from "react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const RESOURCE_LINKS = [
  { to: "/getting-started", label: "Getting started", external: false },
  { to: "/ai-provider-setup", label: "AI provider setup", external: false },
  { to: "/machine-policies", label: "Machine policies", external: false },
  { to: "/ai-privacy-security", label: "AI privacy and security", external: false },
  { to: "https://toolswiki.aec.se/", label: "Wiki", external: true },
] as const;

export function ResourceLinkLabel({ label, external }: { label: string; external: boolean }): ReactElement {
  return (
    <>
      {label}
      {external ? (
        <>
          <ExternalLink className="ml-auto size-3.5" aria-hidden="true" />
          <span className="sr-only"> (opens in a new tab)</span>
        </>
      ) : null}
    </>
  );
}

export function ResourcesMenu(): ReactElement {
  const { pathname } = useLocation();
  const isActive = RESOURCE_LINKS.some((link) => !link.external && link.to === pathname.replace(/\/$/, ""));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="gap-1.5 px-3 font-medium data-[active=true]:bg-accent" data-active={isActive}>
          Resources
          <ChevronDown className="size-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        {RESOURCE_LINKS.map(({ to, label, external }) => (
          <DropdownMenuItem key={to} asChild>
            {external ? (
              <a href={to} target="_blank" rel="noopener noreferrer">
                <ResourceLinkLabel label={label} external />
              </a>
            ) : (
              <Link to={to} aria-current={pathname.replace(/\/$/, "") === to ? "page" : undefined}>
                <ResourceLinkLabel label={label} external={false} />
              </Link>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
